import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useScrollTracking } from "../hooks/useScrollTracking";
import { trackTimeOnPage } from "../utils/analytics";

/**
 * Provider component for automatic app-wide tracking
 * - Tracks scroll depth on every page
 * - Tracks time on page
 * - Sets up automatic click tracking for buttons and links
 *
 * Wrap your app with this component to enable automatic tracking
 *
 * @example
 * <AutoTrackingProvider>
 *   <App />
 * </AutoTrackingProvider>
 */
const AutoTrackingProvider = ({ children, options = {} }) => {
  const location = useLocation();
  const {
    trackScrollDepth = true,
    trackTimeOnPage: enableTimeTracking = true,
    trackClicks = true,
    timeTrackingInterval = 30000, // Track every 30 seconds
  } = options;

  // Track scroll depth
  useScrollTracking({
    enabled: trackScrollDepth,
  });

  // Track time on page
  useEffect(() => {
    if (!enableTimeTracking) return;

    const pageStartTime = Date.now();
    const pageName = location.pathname;

    const timeTrackingIntervalId = setInterval(() => {
      const timeOnPage = Math.round((Date.now() - pageStartTime) / 1000);
      trackTimeOnPage(timeOnPage, pageName);
    }, timeTrackingInterval);

    // Track final time when leaving page
    return () => {
      clearInterval(timeTrackingIntervalId);
      const finalTime = Math.round((Date.now() - pageStartTime) / 1000);
      if (finalTime > 0) {
        trackTimeOnPage(finalTime, pageName);
      }
    };
  }, [location.pathname, enableTimeTracking, timeTrackingInterval]);

  // Automatic click tracking for buttons and links
  useEffect(() => {
    if (!trackClicks) return;

    const handleClick = (e) => {
      const target = e.target;
      const element = target.closest("button, a, [role='button']");

      if (!element) return;

      // Skip if already has tracking
      if (element.dataset.tracked === "true") return;

      const elementName =
        element.textContent?.trim() ||
        element.getAttribute("aria-label") ||
        element.getAttribute("title") ||
        element.id ||
        "unnamed_element";

      const elementId = element.id || null;
      const elementType = element.tagName.toLowerCase();
      const elementLocation = element.closest("header, footer, nav, main")
        ? element.closest("header, footer, nav, main").tagName.toLowerCase()
        : "body";

      if (elementType === "a") {
        const linkUrl = element.href || "";
        // Import here to avoid circular dependencies
        import("../utils/analytics").then(({ trackLinkClick }) => {
          trackLinkClick(elementName, linkUrl, elementLocation);
        });
      } else {
        // Import here to avoid circular dependencies
        import("../utils/analytics").then(({ trackButtonClick }) => {
          trackButtonClick(elementName, elementId, elementLocation, elementType);
        });
      }
    };

    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
    };
  }, [trackClicks]);

  return <>{children}</>;
};

export default AutoTrackingProvider;

